import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { WatchlistEntry } from '@pleco/shared';
import { CHAINS, formatAge, formatPercent, gradeColor, shortAddress } from '@pleco/shared';
import { api, type AlertEvent } from '../lib/api.js';
import { useMapStore } from '../store/mapStore.js';

type Readings = Record<string, { score?: number; cluster?: number; checkedAt?: number }>;

const gradeOf = (s?: number) =>
  s === undefined ? '' : s < 20 ? 'critical' : s < 40 ? 'poor' : s < 60 ? 'fair' : s < 80 ? 'good' : 'excellent';

const KIND_LABEL: Record<AlertEvent['kind'], string> = {
  score_below: 'Score below threshold',
  cluster_above: 'Cluster above threshold',
  score_drop: 'Sharp score drop',
  cluster_growth: 'Cluster growing fast',
};

export function WatchlistPage() {
  const [entries, setEntries] = useState<WatchlistEntry[]>([]);
  const [events, setEvents] = useState<AlertEvent[]>([]);
  const [readings, setReadings] = useState<Readings>({});
  const [channels, setChannels] = useState<string[]>([]);
  const [interval, setIntervalSecs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState<string | 'all' | undefined>();
  const navigate = useNavigate();
  const setToast = useMapStore((s) => s.setToast);

  const refresh = useCallback(async () => {
    try {
      const [w, a] = await Promise.all([api.watchlist(), api.alerts()]);
      setEntries(w.entries);
      setEvents(a.events);
      setReadings(a.readings);
      setChannels(a.channels);
      setIntervalSecs(a.intervalSeconds);
    } catch {
      /* surfaced by empty state */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const run = async (id?: string) => {
    setRunning(id ?? 'all');
    try {
      const { results } = await api.runAlerts(id);
      const fired = results.reduce((a, r) => a + r.fired.length, 0);
      const skipped = results.filter((r) => r.error).length;
      setToast(
        `Checked ${results.length} token${results.length === 1 ? '' : 's'} · ${fired} alert${fired === 1 ? '' : 's'}` +
          (skipped ? ` · ${skipped} skipped` : ''),
      );
      await refresh();
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Check failed');
    } finally {
      setRunning(undefined);
    }
  };

  const testDelivery = async () => {
    try {
      const r = await api.testAlert();
      setToast(r.delivered.length ? `Test sent via ${r.delivered.join(' + ')}` : `Delivery failed on ${r.configured.join(', ')}`);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Test failed');
    }
  };

  return (
    <div className="app-body">
      <div className="main-col scroll-page">
        <div className="page-wrap">
          <div className="page-head">
            <div>
              <h2 className="page-title">Watchlist &amp; alerts</h2>
              <p className="muted page-sub">
                Watched tokens are re-mapped {interval > 0 ? `every ${Math.round(interval / 60)} min` : 'on demand only'}. Alerts
                fire once when a threshold is crossed, and again only after it recovers.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" onClick={() => run()} disabled={!entries.length || running !== undefined}>
                {running === 'all' ? <span className="spinner" /> : '⟳'} Check all now
              </button>
            </div>
          </div>

          <div className="channel-bar">
            <span className="faint">Delivery:</span>
            {channels.length ? (
              channels.map((c) => (
                <span key={c} className="chip accent dot">
                  {c}
                </span>
              ))
            ) : (
              <span className="chip warn">in-app only</span>
            )}
            <span className="faint" style={{ fontSize: 11 }}>
              {channels.length
                ? ''
                : 'Alerts show here. Ask your admin to connect Telegram or a webhook to get them pushed.'}
            </span>
            <div style={{ flex: 1 }} />
            <button className="btn sm" onClick={testDelivery} disabled={!channels.length}>
              Send test
            </button>
          </div>

          {loading && <div className="empty-state">Loading…</div>}
          {!loading && entries.length === 0 && (
            <div className="empty-state">
              Nothing tracked yet. Open any map and press ☆ to watch it.
              <br />
              <button className="tm-btn primary sm" style={{ marginTop: 14 }} onClick={() => navigate('/')}>
                Map a token
              </button>
            </div>
          )}

          {entries.length > 0 && (
            <div className="watch-table">
              <div className="watch-row watch-head">
                <span>Token</span>
                <span>Score</span>
                <span>Top cluster</span>
                <span>Alert if score &lt;</span>
                <span>Alert if cluster &gt;</span>
                <span />
              </div>
              {entries.map((e) => (
                <WatchRow
                  key={e.id}
                  entry={e}
                  reading={readings[e.id]}
                  busy={running === e.id}
                  onRun={() => run(e.id)}
                  onChange={refresh}
                />
              ))}
            </div>
          )}

          <h3 className="section-title">Alert history</h3>
          {events.length === 0 ? (
            <div className="empty-state">No alerts yet.</div>
          ) : (
            <div className="alert-feed">
              {events.map((ev) => (
                <div key={ev.id} className="alert-item">
                  <i
                    className="flag-dot"
                    style={{ background: ev.kind.startsWith('score') ? 'var(--danger)' : 'var(--warn)' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="alert-title">
                      {KIND_LABEL[ev.kind]}
                      <Link to={`/map/${ev.chain}/${ev.address}`} className="chip">
                        {ev.symbol ?? shortAddress(ev.address)}
                      </Link>
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {ev.message}
                    </div>
                  </div>
                  <div className="faint mono" style={{ fontSize: 11, textAlign: 'right' }}>
                    {formatAge(ev.createdAt)} ago
                    <div>{ev.delivered.length ? `sent: ${ev.delivered.join(', ')}` : 'in-app'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WatchRow({
  entry,
  reading,
  busy,
  onRun,
  onChange,
}: {
  entry: WatchlistEntry;
  reading?: { score?: number; cluster?: number; checkedAt?: number };
  busy: boolean;
  onRun: () => void;
  onChange: () => void;
}) {
  const [score, setScore] = useState(entry.alertScoreBelow?.toString() ?? '');
  const [cluster, setCluster] = useState(entry.alertClusterAbove?.toString() ?? '');
  const navigate = useNavigate();
  const setToast = useMapStore((s) => s.setToast);

  const save = async (field: 'alertScoreBelow' | 'alertClusterAbove', raw: string) => {
    const current = field === 'alertScoreBelow' ? entry.alertScoreBelow : entry.alertClusterAbove;
    const value = raw.trim() === '' ? null : Number(raw);
    if (value !== null && (!Number.isFinite(value) || value < 0 || value > 100)) {
      setToast('Thresholds must be between 0 and 100');
      return;
    }
    if ((value ?? undefined) === current) return;
    await api.patchWatch(entry.id, { [field]: value });
    setToast('Threshold saved');
    onChange();
  };

  const grade = gradeOf(reading?.score);

  return (
    <div className="watch-row">
      <span className="watch-token" onClick={() => navigate(`/map/${entry.chain}/${entry.address}`)}>
        <i className="row-swatch" style={{ background: CHAINS[entry.chain]?.color }} />
        <span>
          <strong>{entry.symbol || shortAddress(entry.address)}</strong>
          <span className="faint mono" style={{ display: 'block', fontSize: 11 }}>
            {CHAINS[entry.chain]?.label} · {reading?.checkedAt ? `checked ${formatAge(reading.checkedAt)} ago` : 'not checked yet'}
          </span>
        </span>
      </span>
      <span className="mono" style={{ color: gradeColor(grade) }}>
        {reading?.score !== undefined ? reading.score.toFixed(1) : '—'}
      </span>
      <span className="mono">{reading?.cluster !== undefined ? formatPercent(reading.cluster) : '—'}</span>
      <input
        className="threshold-input"
        inputMode="decimal"
        placeholder="off"
        value={score}
        onChange={(e) => setScore(e.target.value)}
        onBlur={() => void save('alertScoreBelow', score)}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      />
      <input
        className="threshold-input"
        inputMode="decimal"
        placeholder="off"
        value={cluster}
        onChange={(e) => setCluster(e.target.value)}
        onBlur={() => void save('alertClusterAbove', cluster)}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      />
      <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        <button className="btn sm" onClick={onRun} disabled={busy} title="Check now">
          {busy ? <span className="spinner" /> : '⟳'}
        </button>
        <button
          className="btn sm"
          title="Stop watching"
          onClick={() => void api.removeWatch(entry.id).then(onChange)}
        >
          ✕
        </button>
      </span>
    </div>
  );
}
